import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip, Button, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, Divider, FormControl, InputLabel, Select, MenuItem,
  LinearProgress, Tooltip, IconButton, CircularProgress, ListSubheader } from '@mui/material';
import Layout from '../components/Layout';
import ExpandableTable from '../components/ExpandableTable';
import FileUpload from '../components/FileUpload';
import { contractService, projectService } from '../services/api';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';
import PeopleIcon from '@mui/icons-material/People';
import SendIcon from '@mui/icons-material/Send';
import DrawIcon from '@mui/icons-material/Draw';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArticleIcon from '@mui/icons-material/Article';
import BusinessIcon from '@mui/icons-material/Business';

const statusConfig = {
  pending_signatures: { color: '#fff3e0', text: '#e65100', label: 'Pending Signatures' },
  active:             { color: '#e8f5e9', text: '#2e7d32', label: 'Active ✓' },
  completed:          { color: '#e3f2fd', text: '#1565c0', label: 'Completed' },
  cancelled:          { color: '#ffebee', text: '#c62828', label: 'Cancelled' },
};


function ContractExpandedDetail({ contract, user, isHomeownerSigned, isSubSigned, handlers }) {
  const [cos, setCOs] = useState(null);
  const [loadingCOs, setLoadingCOs] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get('/change-orders?contract_id=' + contract.id)
      .then(r => { if (!cancelled) { setCOs(Array.isArray(r.data) ? r.data : []); setLoadingCOs(false); } })
      .catch(() => { if (!cancelled) { setCOs([]); setLoadingCOs(false); } });
    return () => { cancelled = true; };
  }, [contract.id]);

  const approvedCOs = (cos || []).filter(co => co.status === 'approved');
  const totalAdjustment = approvedCOs.reduce((a,c) => a + Number(c.amount || 0), 0);
  const revisedTotal = Number(contract.contract_amount || 0);
  const originalAmount = revisedTotal - totalAdjustment;

  const canSignCO = (co) => {
    if (co.status === 'approved' || co.status === 'rejected') return false;
    if (user?.role === 'homeowner') return !co.homeowner_signed_at;
    if (user?.role === 'general_contractor') return !co.contractor_signed_at;
    if (user?.role === 'subcontractor') return !co.contractor_signed_at;
    return false;
  };

  return (
    <Box>

      {/* Financial summary */}
      <Box sx={{ display: 'flex', mb: 2, background: '#f5f6fa', borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
        <Box sx={{ flex: 1, textAlign: 'center', p: 1.5, borderRight: '1px solid #e0e0e0' }}>
          <Typography variant="caption" sx={{ color: '#666' }}>Original</Typography>
          <Typography sx={{ fontWeight: 700, color: '#1a237e', fontSize: 18 }}>
            ${originalAmount.toLocaleString()}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center', p: 1.5, borderRight: '1px solid #e0e0e0' }}>
          <Typography variant="caption" sx={{ color: '#666' }}>Approved COs ({approvedCOs.length})</Typography>
          <Typography sx={{ fontWeight: 700, color: totalAdjustment >= 0 ? '#2e7d32' : '#c62828', fontSize: 18 }}>
            {totalAdjustment >= 0 ? '+' : ''}${Math.abs(totalAdjustment).toLocaleString()}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center', p: 1.5 }}>
          <Typography variant="caption" sx={{ color: '#666' }}>Revised Total</Typography>
          <Typography sx={{ fontWeight: 800, color: '#1a237e', fontSize: 18 }}>
            ${revisedTotal.toLocaleString()}
          </Typography>
        </Box>
      </Box>

      {/* CO list */}
      {loadingCOs && <Typography variant="caption" sx={{ color: '#999' }}>Loading change orders...</Typography>}
      {!loadingCOs && cos && cos.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ color: '#666', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
            Change orders ({cos.length})
          </Typography>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
            {cos.map((co, idx) => (
              <Box key={co.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderBottom: idx < cos.length - 1 ? '1px solid #f0f0f0' : 'none', background: idx % 2 === 0 ? '#fafbfc' : 'white' }}>
                <Box sx={{ minWidth: 130 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 12, color: '#1a237e' }}>
                    {co.co_number}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666' }} noWrap>{co.title || ''}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: '#999', textTransform: 'capitalize' }}>
                    {(co.type || '').replace(/_/g, ' ')}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 90, textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: Number(co.amount) >= 0 ? '#2e7d32' : '#c62828' }}>
                    {Number(co.amount) >= 0 ? '+' : ''}${Math.abs(Number(co.amount)).toLocaleString()}
                  </Typography>
                </Box>
                <Chip
                  label={co.status === 'approved' ? 'Approved' : co.status === 'rejected' ? 'Rejected' : co.status === 'countered' ? 'Countered' : 'Pending'}
                  size="small"
                  sx={{
                    bgcolor: co.status === 'approved' ? '#e8f5e9' : co.status === 'rejected' ? '#ffebee' : co.status === 'countered' ? '#e3f2fd' : '#fff3e0',
                    color: co.status === 'approved' ? '#2e7d32' : co.status === 'rejected' ? '#c62828' : co.status === 'countered' ? '#1565c0' : '#e65100',
                    fontWeight: 600,
                    fontSize: 11,
                    height: 22,
                    minWidth: 80
                  }}
                />
                <Box sx={{ display: 'flex', gap: 0.5, minWidth: 50, justifyContent: 'center' }}>
                  <Box title={co.homeowner_signed_at ? 'Owner signed' : 'Owner pending'}>
                    {co.homeowner_signed_at
                      ? <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 16 }} />
                      : <PendingIcon sx={{ color: '#f57c00', fontSize: 16 }} />}
                  </Box>
                  <Box title={co.contractor_signed_at ? 'Sub signed' : 'Sub pending'}>
                    {co.contractor_signed_at
                      ? <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 16 }} />
                      : <PendingIcon sx={{ color: '#f57c00', fontSize: 16 }} />}
                  </Box>
                </Box>
                {canSignCO(co) && (
                  <Button size="small" variant="contained"
                    onClick={() => handlers.setCOSignDialog({ open: true, co })}
                    sx={{ bgcolor: '#2e7d32', fontSize: 11, minWidth: 60, '&:hover': { bgcolor: '#1b5e20' } }}>
                    Sign
                  </Button>
                )}
                {(user?.role === 'homeowner' || user?.role === 'general_contractor' || user?.role === 'admin')
                  && co.status !== 'approved' && co.status !== 'rejected' && (
                  <Button size="small" variant="outlined"
                    onClick={() => handlers.handleMarkCOBothSigned(co)}
                    sx={{ borderColor: '#2e7d32', color: '#2e7d32', fontSize: 11, minWidth: 90 }}>
                    Mark Signed
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-start', borderTop: '1px solid #f0f0f0', pt: 1.5 }}>
        <Button size="small" variant="outlined"
          onClick={() => handlers.setViewDialog({ open: true, contract })}
          startIcon={<VisibilityIcon />} sx={{ borderRadius: 2, fontSize: 11 }}>
          View Contract
        </Button>
        {(user?.role === 'homeowner' || user?.role === 'general_contractor') && (
          <Button size="small" variant="outlined" startIcon={<UploadFileIcon />}
            onClick={() => { handlers.setFileDialog({ open: true, contract }); handlers.setFileDialogFiles([]); }}
            sx={{ borderRadius: 2, fontSize: 11, borderColor: '#5e35b1', color: '#5e35b1' }}>
            {contract.contract_file_url ? 'Replace PDF' : 'Attach PDF'}
          </Button>
        )}
        {user?.role === 'homeowner' && !isHomeownerSigned(contract) && (
          <Button size="small" variant="contained" startIcon={<DrawIcon />}
            onClick={() => handlers.setSignDialog({ open: true, contract })}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, fontSize: 11 }}>
            Sign Contract
          </Button>
        )}
        {user?.role === 'homeowner' && isHomeownerSigned(contract) && !isSubSigned(contract) && (
          <Button size="small" variant="outlined" startIcon={<SendIcon />}
            onClick={() => handlers.handleSendForSignature(contract)}
            sx={{ borderRadius: 2, fontSize: 11, borderColor: '#1a237e', color: '#1a237e' }}>
            Send to Vendor
          </Button>
        )}
        {(user?.role === 'homeowner' || user?.role === 'general_contractor') && contract.status === 'pending_signatures' && !contract.docusign_envelope_id && (
          <Button size="small" variant="contained" startIcon={<SendIcon />}
            onClick={() => handlers.handleSendForSignature(contract)}
            sx={{ bgcolor: '#6a1b9a', borderRadius: 2, fontSize: 11 }}>
            Send via DocuSign
          </Button>
        )}
        {(user?.role === 'homeowner' || user?.role === 'general_contractor') && contract.status === 'pending_signatures' && (
          <Button size="small" variant="outlined" startIcon={<CheckCircleIcon />}
            onClick={() => handlers.handleMarkBothSigned(contract)}
            sx={{ borderRadius: 2, fontSize: 11, borderColor: '#2e7d32', color: '#2e7d32' }}>
            Mark as Signed
          </Button>
        )}
        {/* Sub actions: send / resend DocuSign + manual mark signed */}
        {user?.role === 'subcontractor' && contract.status === 'pending_signatures' && !contract.docusign_envelope_id && (
          <Button size="small" variant="contained" startIcon={<SendIcon />}
            onClick={() => handlers.handleSendForSignature(contract)}
            sx={{ bgcolor: '#6a1b9a', borderRadius: 2, fontSize: 11 }}>
            Send via DocuSign
          </Button>
        )}
        {user?.role === 'subcontractor' && contract.status === 'pending_signatures' && !!contract.docusign_envelope_id && (
          <Button size="small" variant="outlined" startIcon={<SendIcon />}
            onClick={() => handlers.handleSendForSignature(contract)}
            sx={{ borderRadius: 2, fontSize: 11, borderColor: '#6a1b9a', color: '#6a1b9a' }}>
            Resend DocuSign
          </Button>
        )}
        {user?.role === 'subcontractor' && contract.status === 'pending_signatures' && (
          <Button size="small" variant="outlined" startIcon={<CheckCircleIcon />}
            onClick={() => handlers.handleMarkBothSigned(contract)}
            sx={{ borderRadius: 2, fontSize: 11, borderColor: '#2e7d32', color: '#2e7d32' }}>
            Mark as Signed
          </Button>
        )}
        {contract.status === 'active' && (
          <Button size="small" variant="contained" startIcon={<AddIcon />}
            onClick={() => handlers.setCODialog({ open: true, contract })}
            sx={{ bgcolor: '#e65100', borderRadius: 2, fontSize: 11 }}>
            New Change Order
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default function Contracts() {
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const navigate = useNavigate();
  const [newContractPicker, setNewContractPicker] = useState({ open: false });
  const [pickerCustomers, setPickerCustomers] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const openNewContractPicker = async () => {
    setNewContractPicker({ open: true });
    setPickerLoading(true);
    try {
      const r = await api.get('/customers');
      setPickerCustomers(r.data || []);
    } catch {}
    setPickerLoading(false);
  };

  const [addressFilter, setAddressFilter] = useState('all');
  const [smartFilter, setSmartFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [tradeOptions, setTradeOptions] = useState([]);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [signDialog, setSignDialog] = useState({ open: false, contract: null });
  const [viewDialog, setViewDialog] = useState({ open: false, contract: null });
  const [uploadForm, setUploadForm] = useState({
    trade_category: '',
    project_id: '', vendor_id: '', contract_amount: '', title: '', contract_file_url: ''
  });
  const [contractFiles, setContractFiles] = useState([]);
  const [fileDialog, setFileDialog] = useState({ open: false, contract: null });
  const [fileDialogFiles, setFileDialogFiles] = useState([]);
  const [fileDialogSaving, setFileDialogSaving] = useState(false);

  const saveContractFile = async () => {
    if (!fileDialog.contract || !fileDialogFiles[0]) return;
    setFileDialogSaving(true);
    try {
      const url = fileDialogFiles[0].url || fileDialogFiles[0].gcsUrl;
      await api.put(`/contracts/${fileDialog.contract.id}/file`, { contract_file_url: url });
      // Refresh contracts list
      const res = await api.get('/contracts');
      setContracts(res.data || []);
      setFileDialog({ open: false, contract: null });
      setFileDialogFiles([]);
    } catch (e) {
      alert('Failed to update contract file');
    }
    setFileDialogSaving(false);
  };
  const [signedName, setSignedName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [coDialog, setCODialog] = useState({ open: false, contract: null });
  const [coListDialog, setCOListDialog] = useState({ open: false, contract: null, cos: [] });
  const [coForm, setCOForm] = useState({ title: '', description: '', type: 'increase', amount: '', file_url: '' });
  const [coFiles, setCOFiles] = useState([]);
  const [coSignDialog, setCOSignDialog] = useState({ open: false, co: null });
  const [coSignedName, setCOSignedName] = useState('');
  const [coAgreed, setCOAgreed] = useState(false);

  useEffect(() => {
    api.get('/settings/trades')
      .then(r => setTradeOptions((r.data || []).filter(t => t.is_active !== false).map(t => t.name)))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [contractRes, projRes] = await Promise.all([
        contractService.getAll(),
        projectService.getAll()
      ]);
      setContracts(contractRes.data);
      setProjects(projRes.data);

      // Poll DocuSign status for pending contracts that have an envelope.
      // Backend finalizes (signed + active + PO) when DocuSign reports completed.
      const pending = (contractRes.data || []).filter(
        c => c.docusign_envelope_id && c.status === 'pending_signatures'
      );
      if (pending.length) {
        let anyCompleted = false;
        await Promise.all(pending.map(async (c) => {
          try {
            const s = await api.get(`/docusign/contracts/${c.id}/status`);
            if (s.data && s.data.status === 'completed') anyCompleted = true;
          } catch (e) {}
        }));
        if (anyCompleted) {
          try {
            const refreshed = await contractService.getAll();
            setContracts(refreshed.data);
          } catch (e) {}
        }
      }
    } catch (err) {}
  };

  const fetchVendors = async (projectId) => {
    try {
      const res = await api.get(`/vendors?project_id=${projectId}`);
      setVendors(res.data.filter(v => v.status === 'approved'));
    } catch (err) { setVendors([]); }
  };

  const handleUpload = async () => {
    if (!uploadForm.project_id || !uploadForm.contract_amount) {
      showAlert('Please fill in required fields', 'error'); return;
    }
    setLoading(true);
    try {
      const payload = {
        project_id: uploadForm.project_id,
        vendor_id: uploadForm.vendor_id || null,
        contract_amount: uploadForm.contract_amount,
        title: uploadForm.title,
        trade_category: uploadForm.trade_category || null,
        contract_file_url: contractFiles[0]?.url || contractFiles[0]?.gcsUrl || '',
      };
      const res = await api.post('/contracts/upload', payload);
      if (res.status === 201 || res.status === 200) {
        setUploadDialog(false);
        setUploadForm({ project_id: '', vendor_id: '', contract_amount: '', title: '', contract_file_url: '' });
        setContractFiles([]);
        setVendors([]);
        showAlert('Contract created! Now sign it and send to your vendor.', 'success');
        await fetchData();
      }
    } catch (err) {
      console.error(err);
      showAlert('Failed to create contract: ' + (err.response?.data?.error || err.message), 'error');
    }
    setLoading(false);
  };

  const handleSign = async () => {
    if (!signedName || !agreed) {
      showAlert('Please type your name and agree to sign', 'error'); return;
    }
    setLoading(true);
    try {
      await api.put(`/contracts/${signDialog.contract.id}/sign`, {
        signed_name: signedName,
        role: user.role
      });
      showAlert('Contract signed successfully!', 'success');
      setSignDialog({ open: false, contract: null });
      setSignedName('');
      setAgreed(false);
      fetchData();
    } catch (err) {
      showAlert('Failed to sign contract', 'error');
    }
    setLoading(false);
  };

  const handleMarkBothSigned = async (contract) => {
    if (!window.confirm('Mark this contract as fully signed (paper/offline)? This will skip e-signing, activate the contract, and create the PO.')) return;
    try {
      await api.put(`/contracts/${contract.id}/mark-both-signed`, { signed_name: 'Paper Signature' });
      showAlert('Contract marked as signed. PO created.', 'success');
      fetchData();
    } catch (err) {
      showAlert('Failed to mark contract as signed', 'error');
    }
  };

  const handleSendForSignature = async (contract) => {
    // Open a blank tab synchronously (must happen in the click handler to avoid popup blockers)
    const popup = window.open('about:blank', '_blank');
    try {
      const r = await api.post(`/docusign/contracts/${contract.id}/prepare`);
      const senderUrl = r.data?.senderUrl;
      if (!senderUrl) throw new Error('No sender URL returned');
      if (popup) {
        popup.location.href = senderUrl;
      } else {
        // Popup blocked — fall back to same-tab
        window.location.href = senderUrl;
      }
      showAlert('Opening DocuSign to add signature fields and send...', 'info');
      // Refresh contracts so the envelope ID is reflected
      contractService.getAll().then(res => setContracts(Array.isArray(res.data) ? res.data : []));
    } catch (err) {
      if (popup) popup.close();
      showAlert('Failed to open DocuSign: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const fetchCOs = async (contractId) => {
    try {
      const res = await api.get('/change-orders?contract_id=' + contractId);
      return res.data;
    } catch (err) { return []; }
  };

  const handleViewCOs = async (contract) => {
    const cos = await fetchCOs(contract.id);
    setCOListDialog({ open: true, contract, cos });
  };

  const handleCreateCO = async () => {
    if (!coForm.title || !coForm.amount) { showAlert('Title and amount are required', 'error'); return; }
    setLoading(true);
    try {
      await api.post('/change-orders', {
        ...coForm,
        contract_id: coDialog.contract.id,
        project_id: coDialog.contract.project_id,
        file_url: coFiles[0] ? (coFiles[0].url || coFiles[0].gcsUrl || '') : '',
      });
      setCODialog({ open: false, contract: null });
      setCOForm({ title: '', description: '', type: 'increase', amount: '', file_url: '' });
      setCOFiles([]);
      showAlert('Change order created!', 'success');
      fetchData();
    } catch (err) { showAlert('Failed to create change order', 'error'); }
    setLoading(false);
  };

  const handleSignCO = async () => {
    if (!coSignedName || !coAgreed) { showAlert('Please sign and agree', 'error'); return; }
    setLoading(true);
    try {
      await api.put('/change-orders/' + coSignDialog.co.id + '/sign', { signed_name: coSignedName, role: user.role });
      showAlert('Change order signed!', 'success');
      setCOSignDialog({ open: false, co: null });
      setCOSignedName(''); setCOAgreed(false);
      fetchData();
      if (coListDialog.open) {
        const cos = await fetchCOs(coListDialog.contract.id);
        setCOListDialog(prev => ({ ...prev, cos }));
      }
    } catch (err) { showAlert('Failed to sign', 'error'); }
    setLoading(false);
  };

  const handleMarkCOBothSigned = async (co) => {
    if (!window.confirm('Mark this change order as fully signed (paper/offline)? This will approve it and update the contract amount + PO budget.')) return;
    try {
      await api.put('/change-orders/' + co.id + '/mark-both-signed', { signed_name: 'Paper Signature' });
      showAlert('Change order marked as signed.', 'success');
      fetchData();
    } catch (err) {
      showAlert('Failed to mark change order as signed', 'error');
    }
  };

  const handleSendCO = async (co) => {
    try {
      await api.post('/change-orders/' + co.id + '/send-for-signature');
      showAlert('Change order sent to contractor!', 'success');
    } catch (err) { showAlert('Failed to send', 'error'); }
  };

  const showAlert = (message, severity) => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 4000);
  };

  const isHomeownerSigned = (c) => c.homeowner_signed_at;
  const isSubSigned = (c) => c.subcontractor_signed_at;

  const matchesSmart = (c) => {
    if (smartFilter === 'all') return true;
    const [kind, val] = smartFilter.split('::');
    if (kind === 'addr') return (c.project_address || c.project_name) === val;
    if (kind === 'cust') return (c.customer_name || '') === val;
    return true;
  };
  const isSubRole = user?.role === 'subcontractor';
  const scopedContracts = isSubRole ? contracts.filter(matchesSmart) : contracts;
  const cardScope = scopedContracts;

  return (
    <Layout>
      {alert.show && <Alert severity={alert.severity} sx={{ mb: 3, borderRadius: 2 }}>{alert.message}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e' }}>Contracts</Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>{contracts.length} total contracts</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {user?.role === 'subcontractor' && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNewContractPicker}
              sx={{ bgcolor: '#1a237e', borderRadius: 2, fontWeight: 700 }}>
              New Contract
            </Button>
          )}
          {(user?.role === 'homeowner' || user?.role === 'general_contractor') && (
            <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => setUploadDialog(true)}
              sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>
              Upload Contract
            </Button>
          )}
        </Box>
      </Box>

      {/* Smart Address/Customer filter for subs */}
      {user?.role === 'subcontractor' && (
        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 320 }}>
            <InputLabel>Address or Customer</InputLabel>
            <Select value={smartFilter} onChange={e => setSmartFilter(e.target.value)} label="Address or Customer" sx={{ borderRadius: 2 }}>
              <MenuItem value="all">All contracts ({contracts.length})</MenuItem>
              <ListSubheader sx={{ fontWeight: 700, color: '#1a237e' }}>By Address</ListSubheader>
              {[...new Set(contracts.map(c => c.project_address || c.project_name).filter(Boolean))].sort().map(a => (
                <MenuItem key={'addr-'+a} value={'addr::'+a} sx={{ pl: 3 }}>{a} ({contracts.filter(c => (c.project_address || c.project_name) === a).length})</MenuItem>
              ))}
              <ListSubheader sx={{ fontWeight: 700, color: '#1a237e' }}>By Customer</ListSubheader>
              {[...new Set(contracts.map(c => c.customer_name).filter(Boolean))].sort().map(cn => (
                <MenuItem key={'cust-'+cn} value={'cust::'+cn} sx={{ pl: 3 }}>{cn} ({contracts.filter(c => c.customer_name === cn).length})</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: cardScope.length, bg: 'linear-gradient(135deg, #1a237e, #283593)' },
          { label: 'Active', value: cardScope.filter(c => c.status === 'active').length, bg: 'linear-gradient(135deg, #2e7d32, #43a047)' },
          { label: 'Pending Signatures', value: cardScope.filter(c => c.status === 'pending_signatures').length, bg: 'linear-gradient(135deg, #e65100, #f57c00)' },
          { label: 'Total Value', value: `$${cardScope.reduce((s, c) => s + Number(c.contract_amount || 0), 0).toLocaleString()}`, bg: 'linear-gradient(135deg, #6a1b9a, #8e24aa)' },
        ].map((card, i) => (
          <Grid item xs={6} md={3} key={i}>
            <Card elevation={0} sx={{ borderRadius: 3, background: card.bg, color: 'white' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{card.value}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>{card.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>


      {/* Contract Cards */}
{/* Filter bar */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {['all', 'pending', 'active'].map(sv => {
            const count = sv === 'all' ? contracts.length : sv === 'pending' ? contracts.filter(c => c.status === 'pending_signatures').length : contracts.filter(c => c.status === 'active').length;
            const label = sv === 'all' ? 'All' : sv.charAt(0).toUpperCase() + sv.slice(1);
            return (
              <Button
                key={sv}
                variant={statusFilter === sv ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setStatusFilter(sv)}
                sx={{
                  textTransform: 'none',
                  borderRadius: 5,
                  px: 2,
                  bgcolor: statusFilter === sv ? '#1a237e' : 'transparent',
                  color: statusFilter === sv ? 'white' : '#1a237e',
                  borderColor: '#1a237e',
                  '&:hover': { bgcolor: statusFilter === sv ? '#0d1340' : '#e8eaf6' }
                }}
              >
                {label} ({count})
              </Button>
            );
          })}
        </Box>
        {user?.role !== 'subcontractor' && (
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Vendor</InputLabel>
          <Select value={vendorFilter} label="Vendor" onChange={(e) => setVendorFilter(e.target.value)}>
            <MenuItem value="all">All vendors</MenuItem>
            {[...new Set(contracts.map(c => c.vendor_name || ((c.first_name || '') + ' ' + (c.last_name || '')).trim()).filter(Boolean))].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())).map(v => (
              <MenuItem key={v} value={v}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>
        )}
      </Box>
      <ExpandableTable
        rows={scopedContracts.filter(c => {
          if (statusFilter === 'pending' && c.status !== 'pending_signatures') return false;
          if (statusFilter === 'active' && c.status !== 'active') return false;
          if (vendorFilter !== 'all') {
            const name = c.vendor_name || ((c.first_name || '') + ' ' + (c.last_name || '')).trim();
            if (name !== vendorFilter) return false;
          }
          return true;
        }).sort((a, b) => {
          const an = (a.vendor_name || ((a.first_name || '') + ' ' + (a.last_name || '')).trim()).toLowerCase();
          const bn = (b.vendor_name || ((b.first_name || '') + ' ' + (b.last_name || '')).trim()).toLowerCase();
          return an.localeCompare(bn);
        })}
        searchPlaceholder="Search by contract #, vendor, address, title..."
        filterRow={(c, q) => (
          (c.contract_number || '').toLowerCase().includes(q) ||
          (c.vendor_name || '').toLowerCase().includes(q) ||
          ((c.first_name || '') + ' ' + (c.last_name || '')).toLowerCase().includes(q) ||
          (c.title || '').toLowerCase().includes(q) ||
          (c.project_address || '').toLowerCase().includes(q) ||
          (c.project_name || '').toLowerCase().includes(q) ||
          (c.po_number || '').toLowerCase().includes(q) ||
          (c.trade_category || '').toLowerCase().includes(q)
        )}
        groupBy={{
          key: 'vendor_name',
          label: 'vendor',
          keyFn: (c) => c.vendor_name || ((c.first_name || '') + ' ' + (c.last_name || '')).trim() || 'Unassigned',
          getCount: (rs) => rs.length,
          getTotal: (rs) => '$' + rs.reduce((a, c) => a + Number(c.contract_amount || 0), 0).toLocaleString(),
          getStatusSummary: (rs) => {
            const active = rs.filter(c => c.status === 'active').length;
            const pending = rs.filter(c => c.status === 'pending_signatures').length;
            const parts = [];
            if (active) parts.push(active + ' active');
            if (pending) parts.push(pending + ' pending');
            return parts.join(' · ') || rs.length + ' contracts';
          }
        }}
        columns={[
          {
            key: 'vendor_name',
            label: isSubRole ? 'Customer / Address' : 'Vendor / Sub',
            render: (c) => (
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a237e' }} noWrap>
                  {isSubRole
                    ? (c.customer_name || c.project_address || c.project_name || '—')
                    : (c.vendor_name || ((c.first_name || '') + ' ' + (c.last_name || '')).trim() || '—')}
                </Typography>
                {(isSubRole ? (c.project_address || c.customer_address) : c.project_address) && (
                  <Typography variant="caption" sx={{ color: '#888' }} noWrap>{isSubRole ? (c.project_address || c.customer_address) : c.project_address}</Typography>
                )}
              </Box>
            )
          },
          {
            key: 'contract_number',
            label: 'Contract #',
            width: 180,
            render: (c) => (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 12, color: '#1a237e' }}>
                  {c.contract_number || c.id.slice(0,8)}
                </Typography>
                {c.title && <Typography variant="caption" sx={{ color: '#666', display: 'block' }} noWrap>{c.title}</Typography>}
              </Box>
            )
          },
          {
            key: 'contract_amount',
            label: 'Amount',
            align: 'right',
            width: 110,
            render: (c) => (
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a237e', fontVariantNumeric: 'tabular-nums' }}>
                ${Number(c.contract_amount || 0).toLocaleString()}
              </Typography>
            )
          },
          {
            key: 'status',
            label: 'Status',
            width: 140,
            render: (c) => (
              <Chip
                label={statusConfig[c.status]?.label || c.status}
                size="small"
                sx={{ bgcolor: statusConfig[c.status]?.color, color: statusConfig[c.status]?.text, fontWeight: 600, height: 22, fontSize: 11 }}
              />
            )
          },
          {
            key: 'signatures',
            label: 'Signed',
            width: 80,
            align: 'center',
            render: (c) => (
              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                <Box title={isHomeownerSigned(c) ? 'Owner signed' : 'Owner pending'}>
                  {isHomeownerSigned(c)
                    ? <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 16 }} />
                    : <PendingIcon sx={{ color: '#f57c00', fontSize: 16 }} />}
                </Box>
                <Box title={isSubSigned(c) ? 'Counterparty signed' : 'Counterparty pending'}>
                  {isSubSigned(c)
                    ? <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 16 }} />
                    : <PendingIcon sx={{ color: '#f57c00', fontSize: 16 }} />}
                </Box>
              </Box>
            )
          },
          {
            key: 'created_at',
            label: 'Date',
            width: 100,
            render: (c) => (
              <Typography variant="caption" sx={{ color: '#666' }}>
                {c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}
              </Typography>
            )
          }
        ]}
        renderExpanded={(contract) => (
          <ContractExpandedDetail
            contract={contract}
            user={user}
            isHomeownerSigned={isHomeownerSigned}
            isSubSigned={isSubSigned}
            handlers={{
              setViewDialog,
              setFileDialog,
              setFileDialogFiles,
              setSignDialog,
              setCODialog,
              setCOSignDialog,
              handleSendForSignature,
              handleMarkBothSigned,
              handleMarkCOBothSigned,
            }}
          />
        )}
        emptyMessage="No contracts match the current filters."
      />

      {/* Upload Contract Dialog */}
      <Dialog open={uploadDialog} onClose={() => setUploadDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white', fontWeight: 700 }}>
          Upload Contract
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Project *</InputLabel>
              <Select value={uploadForm.project_id} label="Project *" sx={{ borderRadius: 2 }}
                onChange={e => { setUploadForm({...uploadForm, project_id: e.target.value, vendor_id: ''}); fetchVendors(e.target.value); }}>
                {projects.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Vendor / Contractor</InputLabel>
              <Select value={uploadForm.vendor_id} label="Vendor / Contractor" sx={{ borderRadius: 2 }}
                onChange={e => setUploadForm({...uploadForm, vendor_id: e.target.value})}>
                <MenuItem value="">No vendor (platform user)</MenuItem>
                {vendors.map(v => <MenuItem key={v.id} value={v.id}>{v.company_name} — {v.trade_specialty}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth label="Contract Title / Description" value={uploadForm.title}
              onChange={e => setUploadForm({...uploadForm, title: e.target.value})}
              placeholder="e.g. Framing Contract - Phase 1"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <TextField select fullWidth label="Trade Category" value={uploadForm.trade_category||''}
              onChange={e => setUploadForm({...uploadForm, trade_category: e.target.value})}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <MenuItem value="">— Select Trade —</MenuItem>
              {(tradeOptions.length ? tradeOptions : ['General','Framing','Foundation','Electrical','Plumbing','HVAC','Roofing','Drywall','Painting','Flooring','Landscaping','Concrete','Masonry','Insulation','Tile','Cabinets','Windows & Doors','Other']).map(t => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </TextField>
            <TextField fullWidth label="Contract Amount ($) *" type="number" value={uploadForm.contract_amount}
              onChange={e => setUploadForm({...uploadForm, contract_amount: e.target.value})}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <Divider />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e' }}>Upload Contract PDF</Typography>
            <FileUpload onFilesChange={setContractFiles} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>

          <Button onClick={() => setUploadDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={loading}
            startIcon={<UploadFileIcon />} sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>
            {loading ? 'Creating...' : 'Create Contract'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sign Contract Dialog */}
      <Dialog open={signDialog.open} onClose={() => setSignDialog({ open: false, contract: null })}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white', fontWeight: 700 }}>
          Sign Contract
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {signDialog.contract && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ bgcolor: '#f5f6fa', borderRadius: 2, p: 2 }}>
                <Typography variant="body2" sx={{ color: '#666' }}>Project</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{signDialog.contract.project_name}</Typography>
                <Typography variant="body2" sx={{ color: '#43a047', fontWeight: 700 }}>
                  ${Number(signDialog.contract.contract_amount).toLocaleString()}
                </Typography>
              </Box>
              {signDialog.contract.contract_file_url && (
                <Button variant="outlined" href={signDialog.contract.contract_file_url} target="_blank"
                  startIcon={<VisibilityIcon />} sx={{ borderRadius: 2 }}>
                  Review Contract PDF Before Signing
                </Button>
              )}
              <Divider />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e' }}>
                Electronic Signature
              </Typography>
              <TextField fullWidth label="Type your full legal name to sign *" value={signedName}
                onChange={e => setSignedName(e.target.value)}
                placeholder="John Smith"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontStyle: 'italic', fontSize: 18 } }} />
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 2, bgcolor: '#f5f6fa', borderRadius: 2 }}>
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                  style={{ marginTop: 3, cursor: 'pointer' }} />
                <Typography variant="body2" sx={{ color: '#666' }}>
                  By typing my name above and checking this box, I agree this constitutes my legal electronic signature 
                  and I agree to the terms of this contract.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => { setSignDialog({ open: false, contract: null }); setSignedName(''); setAgreed(false); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSign} disabled={loading || !signedName || !agreed}
            startIcon={<DrawIcon />} sx={{ bgcolor: '#2e7d32', borderRadius: 2, px: 3 }}>
            {loading ? 'Signing...' : 'Sign Contract'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Change Order Dialog */}
      <Dialog open={coDialog.open} onClose={() => setCODialog({ open: false, contract: null })}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: '#e65100', color: 'white', fontWeight: 700 }}>
          New Change Order
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField fullWidth label="Title *" value={coForm.title}
              onChange={e => setCOForm({...coForm, title: e.target.value})}
              placeholder="e.g. Additional foundation waterproofing"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <TextField fullWidth label="Description" multiline rows={3} value={coForm.description}
              onChange={e => setCOForm({...coForm, description: e.target.value})}
              placeholder="Describe the scope change in detail..."
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={coForm.type} onChange={e => setCOForm({...coForm, type: e.target.value})}
                  label="Type" sx={{ borderRadius: 2 }}>
                  <MenuItem value="increase">Increase (Add Cost)</MenuItem>
                  <MenuItem value="decrease">Decrease (Deduct Cost)</MenuItem>
                </Select>
              </FormControl>
              <TextField fullWidth label="Amount ($) *" type="number" value={coForm.amount}
                onChange={e => setCOForm({...coForm, amount: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Divider />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e' }}>Attach Supporting Documents</Typography>
            <FileUpload onFilesChange={setCOFiles} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => setCODialog({ open: false, contract: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCO} disabled={loading || !coForm.title || !coForm.amount}
            sx={{ bgcolor: '#e65100', borderRadius: 2, px: 3 }}>
            {loading ? 'Creating...' : 'Create Change Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Orders List Dialog */}
      <Dialog open={coListDialog.open} onClose={() => setCOListDialog({ open: false, contract: null, cos: [] })}
        maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white', fontWeight: 700 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Change Orders</span>
            {coListDialog.contract && coListDialog.contract.status === 'active' && (
              <Button size="small" variant="outlined" startIcon={<AddIcon />}
                onClick={() => { setCOListDialog(prev => ({...prev, open: false})); setCODialog({ open: true, contract: coListDialog.contract }); }}
                sx={{ borderColor: 'white', color: 'white', borderRadius: 2 }}>New CO</Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {coListDialog.cos.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: '#999' }}>No change orders yet</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {coListDialog.cos.map(co => (
                <Card key={co.id} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e8eaf6' }}>
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 800, color: '#1a237e' }}>{co.co_number}</Typography>
                        <Chip label={co.status.replace('_', ' ')} size="small"
                          sx={{ fontWeight: 600, textTransform: 'capitalize',
                            bgcolor: co.status === 'approved' ? '#e8f5e9' : co.status === 'pending_approval' ? '#fff3e0' : '#f5f5f5',
                            color: co.status === 'approved' ? '#2e7d32' : co.status === 'pending_approval' ? '#e65100' : '#666' }} />
                        <Chip label={co.type === 'increase' ? 'Add Cost' : 'Deduct'} size="small"
                          sx={{ bgcolor: co.type === 'increase' ? '#fff3e0' : '#e8f5e9',
                            color: co.type === 'increase' ? '#e65100' : '#2e7d32', fontWeight: 600 }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: co.type === 'increase' ? '#e65100' : '#2e7d32' }}>
                        {co.type === 'increase' ? '+' : '-'}${Number(co.amount).toLocaleString()}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{co.title}</Typography>
                    {co.description && <Typography variant="caption" sx={{ color: '#666' }}>{co.description}</Typography>}
                    <Box sx={{ display: 'flex', gap: 2, mt: 1.5, p: 1.5, bgcolor: '#f5f6fa', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {co.homeowner_signed_at
                          ? <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 16 }} />
                          : <PendingIcon sx={{ color: '#f57c00', fontSize: 16 }} />}
                        <Typography variant="caption">Homeowner</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {co.contractor_signed_at
                          ? <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 16 }} />
                          : <PendingIcon sx={{ color: '#f57c00', fontSize: 16 }} />}
                        <Typography variant="caption">Contractor</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                      {co.file_url && (
                        <Button size="small" variant="outlined" href={co.file_url} target="_blank"
                          startIcon={<VisibilityIcon />} sx={{ borderRadius: 2, fontSize: 11 }}>View Doc</Button>
                      )}
                      {user && user.role === 'homeowner' && !co.homeowner_signed_at && co.status !== 'approved' && (
                        <Button size="small" variant="contained" startIcon={<DrawIcon />}
                          onClick={() => setCOSignDialog({ open: true, co })}
                          sx={{ bgcolor: '#1a237e', borderRadius: 2, fontSize: 11 }}>Sign</Button>
                      )}
                      {user && user.role === 'homeowner' && co.homeowner_signed_at && !co.contractor_signed_at && (
                        <Button size="small" variant="outlined" startIcon={<SendIcon />}
                          onClick={() => handleSendCO(co)}
                          sx={{ borderRadius: 2, fontSize: 11, borderColor: '#1a237e', color: '#1a237e' }}>
                          Send to Contractor
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCOListDialog({ open: false, contract: null, cos: [] })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Sign CO Dialog */}
      <Dialog open={coSignDialog.open} onClose={() => setCOSignDialog({ open: false, co: null })}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: '#2e7d32', color: 'white', fontWeight: 700 }}>Sign Change Order</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {coSignDialog.co && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ bgcolor: '#f5f6fa', borderRadius: 2, p: 2 }}>
                <Typography variant="body2" sx={{ color: '#666' }}>{coSignDialog.co.co_number}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{coSignDialog.co.title}</Typography>
              </Box>
              <TextField fullWidth label="Type your full legal name *" value={coSignedName}
                onChange={e => setCOSignedName(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontStyle: 'italic' } }} />
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 2, bgcolor: '#f5f6fa', borderRadius: 2 }}>
                <input type="checkbox" checked={coAgreed} onChange={e => setCOAgreed(e.target.checked)} style={{ marginTop: 3, cursor: 'pointer' }} />
                <Typography variant="body2" sx={{ color: '#666' }}>
                  By typing my name and checking this box, I approve this change order.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => { setCOSignDialog({ open: false, co: null }); setCOSignedName(''); setCOAgreed(false); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSignCO} disabled={loading || !coSignedName || !coAgreed}
            startIcon={<DrawIcon />} sx={{ bgcolor: '#2e7d32', borderRadius: 2, px: 3 }}>
            {loading ? 'Signing...' : 'Sign Change Order'}
          </Button>
        </DialogActions>
      </Dialog>
    
      {/* View Contract PDF Dialog */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false, contract: null })}
        maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#1a237e', color: 'white', fontWeight: 700, pr: 1 }}>
          <Box>
            {viewDialog.contract?.contract_number || 'Contract'}
            {viewDialog.contract?.title && (
              <Typography variant="caption" sx={{ display: 'block', color: '#bbb', fontWeight: 400 }}>
                {viewDialog.contract.title}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setViewDialog({ open: false, contract: null })} sx={{ color: 'white' }}>
            <Box component="span" sx={{ fontSize: 20 }}>&times;</Box>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {(() => {
            const url = viewDialog.contract?.signed_contract_url || viewDialog.contract?.contract_file_url;
            if (!url) {
              return (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4, color: '#666' }}>
                  <Typography>No contract file attached yet.</Typography>
                </Box>
              );
            }
            return (
              <Box sx={{ flex: 1, position: 'relative' }}>
                <iframe
                  src={url}
                  title="Contract PDF"
                  style={{ width: '100%', height: '100%', border: 'none', minHeight: '70vh' }}
                />
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          {(viewDialog.contract?.signed_contract_url || viewDialog.contract?.contract_file_url) && (
            <Button
              variant="outlined"
              href={viewDialog.contract?.signed_contract_url || viewDialog.contract?.contract_file_url}
              target="_blank"
              sx={{ borderRadius: 2 }}
            >
              Open in new tab
            </Button>
          )}
          <Button variant="contained" onClick={() => setViewDialog({ open: false, contract: null })} sx={{ bgcolor: '#1a237e', borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Attach / Replace Contract PDF Dialog */}
      <Dialog open={fileDialog.open} onClose={() => { setFileDialog({ open: false, contract: null }); setFileDialogFiles([]); }} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #e0e0e0', pb: 2 }}>
          {fileDialog.contract?.contract_file_url ? 'Replace Contract PDF' : 'Attach Contract PDF'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 2, color: '#666', fontSize: 14 }}>
            {fileDialog.contract?.contract_file_url
              ? 'This will replace the currently-attached PDF on this contract. Existing signed PDFs (after DocuSign completion) are not affected.'
              : 'Upload a contract PDF to attach to this contract before sending for signatures.'}
          

</Box>
          <FileUpload onFilesChange={setFileDialogFiles} />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0', gap: 1 }}>
          <Button onClick={() => { setFileDialog({ open: false, contract: null }); setFileDialogFiles([]); }} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveContractFile}
            disabled={fileDialogSaving || !fileDialogFiles[0]}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>
            {fileDialogSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={newContractPicker.open} onClose={() => setNewContractPicker({ open: false })} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white', fontWeight: 700 }}>Pick a Customer</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {pickerLoading ? <CircularProgress /> :
            pickerCustomers.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography sx={{ color: '#666', mb: 2 }}>No customers yet.</Typography>
                <Button variant="contained" onClick={() => { setNewContractPicker({ open: false }); navigate('/customers'); }}
                  sx={{ bgcolor: '#1a237e', borderRadius: 2 }}>
                  Add a Customer First
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                {pickerCustomers.map(pc => (
                  <Card key={pc.id} sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f5f6fa' } }}
                    onClick={() => { setNewContractPicker({ open: false }); navigate('/customers/' + pc.id + '/new-contract'); }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography sx={{ fontWeight: 700, color: '#1a237e' }}>{pc.name}</Typography>
                      {pc.contact_name && <Typography sx={{ fontSize: 12, color: '#888' }}>{pc.contact_name}</Typography>}
                      <Typography sx={{ fontSize: 13, color: '#666' }}>{pc.address || pc.email}</Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setNewContractPicker({ open: false })}>Cancel</Button>
          <Button onClick={() => { setNewContractPicker({ open: false }); navigate('/customers'); }}>Manage Customers</Button>
        </DialogActions>
      </Dialog>

      </Layout>
  );
}